import { ReactIcons, VideoControlsOverlay } from "@kalyzee/kalyzee-ui";
import { PlayerWorkBox, PlayerWorkBoxProps } from "./PlayerWorkBox";
import { MeetingSession, MeetingSessionHighlightingState, MeetingSessionMedia, MeetingState } from "@kalyzee/kast-web-sdk/websocket";
import { isSuperUser, MeetingContainerAPI, useMeetingSelector } from "@kalyzee/kast-web-sdk";
import React, { useCallback } from "react";
import { getNormalUser } from "../../helpers/user";
import { Touchable } from "@kalyzee/kast-web-sdk/web-components";

import styles from "./MasterPlayerWorkBox.module.css";

export interface MasterPlayerWorkBoxProps {
  meetingAPI: MeetingContainerAPI;
  children: React.ReactNode;
  session: MeetingSession;
  mediaSession: MeetingSessionMedia;
  overlayMode?: PlayerWorkBoxProps["overlayMode"];
  isSmall?: boolean;
  isPlayingSound?: boolean;
  overlayContent?: {
    type: "controls";
    onRight?: (on: boolean) => void;
    onLeft?: (on: boolean) => void;
    onUp?: (on: boolean) => void;
    onDown?: (on: boolean) => void;
    onZoomIn?: (on: boolean) => void;
    onZoomOut?: (on: boolean) => void;
  } | {
    type: "selection",
    onSelect?: () => void;
    icon?: React.ReactNode;
  },
  mediaButtons?: boolean,
  fullscreenButton?: boolean,
  extraHeaderContent?: React.ReactNode,
  className?: string,
  topBarClassName?: string;
  nativeProps?: React.HTMLAttributes<HTMLDivElement>;
}

export const MasterPlayerWorkBox = React.memo(({
  meetingAPI,
  children,
  session,
  mediaSession,
  overlayMode,
  overlayContent,
  isSmall,
  isPlayingSound,
  mediaButtons,
  fullscreenButton,
  extraHeaderContent,
  className,
  topBarClassName,
  nativeProps,
}: MasterPlayerWorkBoxProps) => {
  const [meetingOngoing] = useMeetingSelector(meetingAPI, "meeting", meeting => meeting?.state === MeetingState.RUNNING);
  const [sessionMe] = useMeetingSelector(meetingAPI, "session");
  const isMe = sessionMe?.id === session.id;
  const { displayName, avatar } = session.user ? getNormalUser(session.user) : { displayName: undefined, avatar: undefined };

  const renderOverlayContent = () => {
    if (!overlayContent) return null;
    if (overlayContent.type === "selection") {
      return (
        <Touchable
          onClick={overlayContent.onSelect}
          className={styles.selection}
        >
          {overlayContent.icon ?? <ReactIcons.Expand style={{ color: "white" }} />}
        </Touchable>
      );
    }
    return (
      <VideoControlsOverlay
        onRight={overlayContent.onRight}
        onLeft={overlayContent.onLeft}
        onUp={overlayContent.onUp}
        onDown={overlayContent.onDown}
        onZoomIn={overlayContent.onZoomIn}
        onZoomOut={overlayContent.onZoomOut}
      >
        <></>
      </VideoControlsOverlay>
    );
  }

  const getTopBarLabel = () => {
    if (isMe) return "Vous";
    return displayName ?? "Utilisateur";
  };

  const getTobBarLabelInfo = () => {
    if (isSmall) return undefined;
    let label = mediaSession.extra?.label ?? "webcam";
    return label.charAt(0).toUpperCase() + label.slice(1);
  };

  const enableControls = mediaButtons && (isMe || isSuperUser(sessionMe));

  const videoEnabled = mediaSession.video?.enabled ?? false;
  const audioEnabled = mediaSession.audio?.enabled ?? false;

  const onVideo = useCallback(() => {
    const { updater } = meetingAPI;
    updater.medias.update(session.id, mediaSession.id, {
      video: {
        enabled: !videoEnabled,
      },
    });
  }, [meetingAPI, session.id, mediaSession.id, videoEnabled]);

  const onAudio = useCallback(() => {
    const { updater } = meetingAPI;
    updater.medias.update(session.id, mediaSession.id, {
      audio: {
        enabled: !audioEnabled,
      },
    });
  }, [meetingAPI, session.id, mediaSession.id, audioEnabled]);

  return (
    <PlayerWorkBox
      label={getTopBarLabel()}
      userDisplayName={displayName}
      avatar={avatar}
      labelInfo={getTobBarLabelInfo()}
      overlayMode={overlayMode}
      overlayContent={renderOverlayContent()}
      extraHeaderContent={extraHeaderContent}
      isScreenSource={mediaSession.extra?.type === "screen"}
      className={className}
      topBarClassName={topBarClassName}
      video={videoEnabled}
      audio={audioEnabled}
      onChangeVideo={enableControls ? onVideo : undefined}
      onChangeAudio={enableControls ? onAudio : undefined}
      fullscreenButton={fullscreenButton}
      isMe={isMe}
      isSmall={isSmall}
      isHighlighted={session.highlighting === MeetingSessionHighlightingState.ONGOING}
      isPlayingSound={isPlayingSound}
      nativeProps={nativeProps}
      idleAnimation={!meetingOngoing}
      mediaButtons={mediaButtons}
    >
      {children}
    </PlayerWorkBox>
  );
});
